import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { Box, FormHelperText, Stack, TextField } from '@mui/material';
import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Controller, useFormContext } from 'react-hook-form';

interface FormCounterProps {
    id?: string;
    name: string;
    disabled: boolean;
    max?: number;
    align?: 'flex-start' | 'flex-end';
    disableDecrease?: boolean;
    /** When true, only the + control is disabled (e.g. at max); field and − stay usable. */
    disableIncrement?: boolean;
}

/**
 * For use within RHF FormProvider
 */
const FormCounter = ({
    id,
    name,
    disabled,
    max,
    align = 'flex-end',
    disableDecrease = false,
    disableIncrement = false,
}: FormCounterProps) => {
    const { control, setValue, watch } = useFormContext();
    const theme = useTheme();

    const value: number = watch(name);

    const clampToMax = (n: number) => {
        if (max == null || !Number.isFinite(max)) {
            return n;
        }
        return Math.min(n, max);
    };

    const atMax = max != null && Number.isFinite(max) && (value ?? 0) >= max;
    const effectiveDisableIncrement = disableIncrement || atMax;

    const handleIncrement = () => {
        if (disabled || effectiveDisableIncrement) return;
        if (value === undefined) {
            setValue(name, 0, { shouldValidate: true, shouldDirty: true });
            return;
        }
        setValue(name, clampToMax(value + 1), {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const handleDecrement = () => {
        if (disabled) return;
        if (value === undefined) {
            setValue(name, 0, { shouldValidate: true, shouldDirty: true });
            return;
        }
        setValue(name, value - 1, { shouldValidate: true, shouldDirty: true });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) return;
        if (e.target.value === '') {
            setValue(name, undefined);
            return;
        }

        setValue(name, clampToMax(e.target.valueAsNumber), {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    // if users clicks off while input empty, replace with 0
    const handleBlur = () => {
        if (value === undefined) {
            setValue(name, 0, { shouldValidate: true, shouldDirty: true });
            return;
        }
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { ref }, fieldState: { error } }) => (
                <Stack sx={{ width: 'fit-content', alignItems: align }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            width: 92,
                            height: 26,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'custom.lightBorderColor',
                            bgcolor: 'background.paper',
                            boxShadow: `0 6px 18px ${alpha(
                                theme.palette.text.primary,
                                0.08,
                            )}`,
                        }}
                    >
                        <Box
                            component='button'
                            type='button'
                            onClick={handleDecrement}
                            disabled={
                                disabled || value === 0 || disableDecrease
                            }
                            style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                width: 30,
                                cursor: 'pointer',
                            }}
                            aria-label='Decrease'
                            className='FormCounter-decrement'
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'text.secondary',
                                    '& svg': { fontSize: 16 },
                                    '&:disabled': {
                                        cursor: 'not-allowed',
                                        color: 'text.disabled',
                                    },
                                }}
                            >
                                <RemoveCircleOutline />
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                width: 32,
                                borderLeft: '1px solid',
                                borderRight: '1px solid',
                                borderColor: 'custom.lightBorderColor',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <TextField
                                id={id}
                                error={!!error}
                                inputRef={ref}
                                variant='standard'
                                size='small'
                                type='number'
                                value={value ?? ''}
                                disabled={disabled}
                                inputProps={{
                                    min: 0,
                                    ...(max != null ? { max } : {}),
                                    style: {
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        fontSize: 12,
                                        lineHeight: '14px',
                                    },
                                }}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                sx={{
                                    width: '100%',
                                    '& .MuiInput-root:before, & .MuiInput-root:after':
                                        { display: 'none' },
                                    '& .MuiInputBase-root': {
                                        height: 26,
                                        px: 0,
                                    },
                                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                                        {
                                            WebkitAppearance: 'none',
                                            margin: 0,
                                        },
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                }}
                            />
                        </Box>

                        <Box
                            component='button'
                            type='button'
                            onClick={handleIncrement}
                            disabled={disabled || effectiveDisableIncrement}
                            style={{
                                border: 'none',
                                padding: 0,
                                width: 30,
                                cursor: 'pointer',
                            }}
                            aria-label='Increase'
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor:
                                        disabled || effectiveDisableIncrement
                                            ? theme.palette.action
                                                  .disabledBackground
                                            : theme.palette.primary.main,
                                    color:
                                        disabled || effectiveDisableIncrement
                                            ? theme.palette.text.disabled
                                            : theme.palette.common.white,
                                    '& svg': { fontSize: 16 },
                                    '&:disabled': {
                                        cursor: 'not-allowed',
                                    },
                                }}
                            >
                                <AddCircleOutline />
                            </Box>
                        </Box>
                    </Box>
                    {!!error && (
                        <FormHelperText error>{error.message}</FormHelperText>
                    )}
                </Stack>
            )}
        />
    );
};

export default FormCounter;
